import { assert, subTestContext, Test } from "oc-e2e-testrunner";
import { resetArrangement, assertWarnungen, getInspiredArrangement } from "./utils";
import { Arrangement } from "../arrangement/Arrangement";

/**
 * Testet Stückliste (Bill of Materials), verschiedene Detailserien und die Torpfosten-Varianten
 * für Schiebetore.
 *
 * Bisher gab es in TestStückliste.ts keinen einzigen Schiebetor-Fall, und die Torpfosten wurden
 * nur am Rande (geteilter Pfosten mit Einzeltor) geprüft.
 *
 * Die Stücklisten-Assertions sind bewusst struktur-/invarianten-basiert (z.B. genau ein
 * "3811…"-Schiebetor-Set-Artikel), da die exakten Begleitartikel-IDs serienabhängig sind.
 */
export default {
  name: "TestSchiebetorStückliste",
};

const find = (k: string) => APP.richMetadata.findAuswahlwert(k);

async function createSchiebetorInStrecke(breite = 8): Promise<Arrangement> {
  resetArrangement();
  const agt = APP.arrangement as Arrangement;
  agt.grundriss.setStandardForm("Strecke", [breite]);
  agt.update3d();
  agt.setSelectedObjects(agt.getEmpties());
  await agt.changeSelectedZaunElemente({ massAuswahlwert: find("Schiebetor_Easy") });
  agt.update3d();
  agt.update3d(); // 2. Lauf: Stückliste/Begleitartikel vollständig
  return agt;
}

const schiebetorSetArtikel = (agt: Arrangement) =>
  agt.bestellliste.filter((e) => String((e as any).ArtikelID).startsWith("3811"));

// ---------------------------------------------------------------------------
// 1) Die Bestellliste enthält genau einen Schiebetor-Set-Artikel (3811…)
// ---------------------------------------------------------------------------
export const testStücklisteEnthältSchiebetorSet: Test = async () => {
  const agt = await createSchiebetorInStrecke();
  assert.isTrue(agt.getTore()[0].isSchiebetor, "ist Schiebetor");
  assertWarnungen(agt, []);
  const sets = schiebetorSetArtikel(agt);
  assert.equals(1, sets.length, "genau ein 3811…-Schiebetor-Set in der Bestellliste");
  assert.isTrue((sets[0] as any).Anzahl >= 1, "Anzahl des Schiebetor-Sets >= 1");
};

// ---------------------------------------------------------------------------
// 2) Torpfosten eines Schiebetores: genau ein Einzel- und ein Doppel-Schiebetorpfosten
// ---------------------------------------------------------------------------
export const testSchiebetorHatEinzelUndDoppelpfosten: Test = async () => {
  const agt = await createSchiebetorInStrecke();
  const tor = agt.getTore()[0];
  const pfosten = tor.getPfosten().filter((p) => p != null);
  assert.equals(2, pfosten.length, "Schiebetor hat zwei Pfosten");
  pfosten.forEach((p) =>
    assert.isTrue(p.isSchiebetorpfosten, `Pfosten ${p.meta?.Key} muss Schiebetorpfosten sein`)
  );
  assert.equals(
    1,
    pfosten.filter((p) => p.isSchiebetorEinzelpfosten).length,
    "genau ein Einzel-Schiebetorpfosten (Anschlagseite)"
  );
  assert.equals(
    1,
    pfosten.filter((p) => p.isSchiebetorDoppelpfosten).length,
    "genau ein Doppel-Schiebetorpfosten (Laufseite)"
  );
  // Schiebetorpfosten zählen als Torpfosten.
  assert.isTrue(
    pfosten.every((p) => p.isTorpfosten),
    "Schiebetorpfosten müssen auch isTorpfosten erfüllen"
  );
};

// ---------------------------------------------------------------------------
// 3) Die globale Pfostenart darf Schiebetorpfosten NICHT ersetzen
// ---------------------------------------------------------------------------
export const testGlobalePfostenartÄndertSchiebetorpfostenNicht: Test = async () => {
  const agt = await createSchiebetorInStrecke();
  const schiebetorpfostenSindKorrekt = () =>
    agt
      .getTore()[0]
      .getPfosten()
      .filter((p) => p != null)
      .every((p) => p.isSchiebetorpfosten);

  for (const art of ["Steckpfosten", "Metallpfosten", "Klemmpfosten"]) {
    await subTestContext(`Pfostenart=${art}`, async () => {
      (agt.auswahlAccessoren.Pfostenart as any).setter(find(art));
      agt.update3d();
      assert.isTrue(
        schiebetorpfostenSindKorrekt(),
        `Schiebetorpfosten müssen trotz globaler Pfostenart "${art}" erhalten bleiben`
      );
    });
  }
};

// ---------------------------------------------------------------------------
// 4) Pfostenfarbe Anthrazit/Silber wählt die passenden Schiebetorpfosten-Keys
// ---------------------------------------------------------------------------
export const testSchiebetorpfostenFarbenKeys: Test = async () => {
  const agt = await createSchiebetorInStrecke();
  const tor = agt.getTore()[0];
  const setzeFarbe = (farbe: string) => {
    tor.getPfosten()
      .filter((p) => p != null && p.isSchiebetorpfosten)
      .forEach((p) => (p.pfostenfarbeAuswahlwert = find(farbe)));
    agt.update3d();
  };

  setzeFarbe("Silber");
  tor.getPfosten()
    .filter((p) => p != null && p.isSchiebetorpfosten)
    .forEach((p) =>
      assert.isTrue(
        /Silber/.test(p.meta?.Key ?? ""),
        `Bei Pfostenfarbe Silber erwartet ein "…Silber"-Key, ist: ${p.meta?.Key}`
      )
    );

  setzeFarbe("Anthrazit");
  tor.getPfosten()
    .filter((p) => p != null && p.isSchiebetorpfosten)
    .forEach((p) =>
      assert.isTrue(
        /Anthrazit/.test(p.meta?.Key ?? ""),
        `Bei Pfostenfarbe Anthrazit erwartet ein "…Anthrazit"-Key, ist: ${p.meta?.Key}`
      )
    );
};

// ---------------------------------------------------------------------------
// 5) Schiebetor in verschiedenen Detailserien (Inspirationen)
// ---------------------------------------------------------------------------
export const testSchiebetorInVerschiedenenDetailserien: Test = async () => {
  const inspirationen = [
    "Inspiration SYSTEM WPC CLASSIC Anthrazit",
    "Inspiration SYSTEM RHOMBUS HOLZ Thermo",
    "Inspiration SYSTEM WPC PLATINUM XL Taupe",
    "Inspiration SYSTEM NEO WPC PLATINUM Braun",
  ];
  let geprüft = 0;
  for (const insp of inspirationen) {
    if (!APP.richMetadata.findInspiration(insp)) continue; // Serie in diesen Daten nicht vorhanden
    await subTestContext(insp, async () => {
      const agt = getInspiredArrangement(insp) as Arrangement;
      agt.grundriss.setStandardForm("Strecke", [8]);
      agt.update3d();
      agt.setSelectedObjects(agt.getEmpties());
      await agt.changeSelectedZaunElemente({ massAuswahlwert: find("Schiebetor_Easy") });
      agt.update3d();
      agt.update3d();

      const tor = agt.getTore()[0];
      assert.isTrue(tor.isSchiebetor, "ist Schiebetor");
      assert.isNotNull(tor.schiebetorRahmenDef, "RahmenDef vorhanden");
      assert.isTrue(tor.schiebetorRahmenDef.füllungsFenster.length >= 1, "hat Füllungsfenster");
      // Jede Serie erzeugt genau einen Schiebetor-Set-Artikel.
      assert.equals(1, schiebetorSetArtikel(agt).length, "ein Schiebetor-Set pro Serie");
      assertWarnungen(agt, []);
      geprüft++;
    });
  }
  assert.isTrue(geprüft > 0, "Mindestens eine Detailserie muss vorhanden und geprüft worden sein");
};
